import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import { analysisManagerFiltersAtom, hlFilteredPairsAtom } from 'r/atoms';
import { MultiSelectComboBox } from 'r/ui';
import type { MultiSelectOption } from 'r/ui/MultiSelectComboBox';
import SyncCheckButton from './SyncCheckButton';
import { AuthorFilterDropdown } from './AuthorFilterDropdown';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AnalysisManagerFiltersProps {
  isFetching: boolean;
}

export default function AnalysisManagerFilters({
  isFetching,
}: AnalysisManagerFiltersProps) {
  const [filters, setFilters] = useAtom(analysisManagerFiltersAtom);
  const pairs = useAtomValue(hlFilteredPairsAtom);

  const [localSearch, setLocalSearch] = useState(filters.searchQuery);

  const pairsPrepared: MultiSelectOption[] = useMemo(
    () =>
      pairs.map((pair) => ({
        id: pair.symbol,
        label: pair.symbol,
        icon: pair.logoUrl,
      })),
    [pairs],
  );

  const debouncedSetSearch = useCallback(
    debounce((searchValue: string) => {
      setFilters((prev) => ({
        ...prev,
        searchQuery: searchValue,
        currentPage: 1,
      }));
    }, 750),
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchClear = () => {
    debouncedSetSearch.cancel();
    setLocalSearch('');
    setFilters((prev) => ({ ...prev, searchQuery: '', currentPage: 1 }));
  };

  const handleAuthorFilterModeChange = (mode: 'all' | 'kols') => {
    setFilters((prev) => ({
      ...prev,
      authorFilterMode: mode,
      excludedAuthors: [],
      currentPage: 1,
    }));
  };

  const handleExcludedAuthorsChange = (excluded: string[]) => {
    setFilters((prev) => ({
      ...prev,
      excludedAuthors: excluded,
      currentPage: 1,
    }));
  };

  const handleAuthorFilterReset = () => {
    setFilters((prev) => ({
      ...prev,
      authorFilterMode: 'all',
      excludedAuthors: [],
      currentPage: 1,
    }));
  };

  const handlePairChange = (value: MultiSelectOption[]) => {
    setFilters((prev) => ({
      ...prev,
      selectedPair: value,
      currentPage: 1,
    }));
  };

  return (
    <div className="flex flex-col justify-between items-start gap-4 mb-6">
      <div className="flex items-center justify-between w-full">
        <h2 className="text-[20px] text-main font-medium">Analyses</h2>
        <div className="hidden">
          <SyncCheckButton />
        </div>
      </div>

      <div className="flex gap-8 w-full xl:w-auto">
        <div className="flex flex-col gap-3 w-full xl:w-auto">
          {/* Author Filter */}
          <AuthorFilterDropdown
            filterMode={filters.authorFilterMode}
            excludedAuthors={filters.excludedAuthors}
            onFilterModeChange={handleAuthorFilterModeChange}
            onExcludedAuthorsChange={handleExcludedAuthorsChange}
            onReset={handleAuthorFilterReset}
            isDisabled={isFetching}
          />

          {/* Pairs Filter */}
          <MultiSelectComboBox
            label="Pairs"
            placeholder="Select pairs"
            searchPlaceholder="Search for pair..."
            selectedItems={filters.selectedPair || []}
            items={pairsPrepared}
            onSelectionChange={handlePairChange}
            isDisabled={isFetching}
            fuseKeys={['label']}
            width={320}
          />

          {/* Show Deleted Only Checkbox */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-deleted"
              checked={filters.showDeletedOnly}
              onCheckedChange={(checked) =>
                setFilters((prev) => ({
                  ...prev,
                  showDeletedOnly: checked as boolean,
                  currentPage: 1,
                }))
              }
              disabled={isFetching}
            />
            <Label htmlFor="show-deleted" className="text-sm text-secondary">
              Show deleted only
            </Label>
          </div>
        </div>

        <div className="flex flex-col items-start gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            {/* Sorting Selector */}
            <div className="sm:w-48">
              <Label className="mb-2 block">Sort by</Label>
              <Select
                value={filters.sortBy}
                onValueChange={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    sortBy: value,
                    currentPage: 1,
                  }));
                }}
                disabled={isFetching}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt_desc">
                    Created At (Newest)
                  </SelectItem>
                  <SelectItem value="createdAt_asc">
                    Created At (Oldest)
                  </SelectItem>
                  <SelectItem value="tweetDate_desc">
                    Tweet Date (Newest)
                  </SelectItem>
                  <SelectItem value="tweetDate_asc">
                    Tweet Date (Oldest)
                  </SelectItem>
                  <SelectItem value="emergingScore_desc">
                    Emerging Score (High to Low)
                  </SelectItem>
                  <SelectItem value="emergingScore_asc">
                    Emerging Score (Low to High)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter Type Selector */}
            <div className="sm:w-48">
              <Label className="mb-2 block">Filter by source</Label>
              <Select
                value={filters.filterType}
                onValueChange={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    filterType: value as 'all' | 'api' | 'manual',
                    currentPage: 1,
                  }));
                }}
                disabled={isFetching}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">API + Manual</SelectItem>
                  <SelectItem value="api">API Only</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search Input */}
            <div className="flex-1 sm:flex-none sm:w-64">
              <Label className="mb-2 block">Search analyses</Label>
              <div className="relative">
                <Input
                  aria-label="Search by author or text"
                  placeholder="Search by author or text..."
                  value={localSearch}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  disabled={isFetching}
                  className="w-full"
                />
                {localSearch && (
                  <button
                    onClick={handleSearchClear}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            {/* Date Filters */}
            <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto items-end">
              <div className="w-full sm:w-40">
                <Label className="mb-2 block">Date filter</Label>
                <Select
                  value={filters.dateFilterType}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateFilterType: value as
                        | 'all'
                        | 'createdAt'
                        | 'tweetDate',
                      startDate: '',
                      endDate: '',
                      currentPage: 1,
                    }))
                  }
                  disabled={isFetching}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dates</SelectItem>
                    <SelectItem value="createdAt">Created Date</SelectItem>
                    <SelectItem value="tweetDate">Tweet Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-40">
                <Label className="mb-2 block">Start date</Label>
                <DatePicker
                  value={
                    filters.startDate ? new Date(filters.startDate) : undefined
                  }
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      startDate: date ? date.toISOString() : '',
                      currentPage: 1,
                    }))
                  }
                  maxDate={
                    filters.endDate ? new Date(filters.endDate) : undefined
                  }
                  disabled={isFetching || filters.dateFilterType === 'all'}
                  placeholder="Start date"
                />
              </div>

              <div className="w-full sm:w-40">
                <Label className="mb-2 block">End date</Label>
                <DatePicker
                  value={
                    filters.endDate ? new Date(filters.endDate) : undefined
                  }
                  onChange={(date) =>
                    setFilters((prev) => ({
                      ...prev,
                      endDate: date ? date.toISOString() : '',
                      currentPage: 1,
                    }))
                  }
                  minDate={
                    filters.startDate ? new Date(filters.startDate) : undefined
                  }
                  disabled={isFetching || filters.dateFilterType === 'all'}
                  placeholder="End date"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
